import isEqual from "lodash/isEqual";
import { isValidPercentage, offsetTextByConstant } from "../../util/general";
import { mod } from "../../util/basicAlgebra";

import {
  getBoxVariantCssClassName,
  globalizedBoxVariantSelectors,
} from "../boxVariant/boxVariant";

import * as fromSpinURL from "../spinURL/spinURL";
import * as fromFolderURL from "../folderURL/folderURL";
import * as fromUI from "../ui/ui";
import * as fromHotspotEditor from "../hotspotEditor/hotspotEditorSelectors";
import * as fromPointerVariant from "../pointerVariant/pointerVariant";
import * as fromBoxVariant from "../boxVariant/boxVariant";

import { getTemplateFromVariantStyles } from "./variantStyles";
import {
  hoverActions,
  staticImageSnippetTypes,
  versions,
} from "../../util/constants";
import { forEach } from "lodash";
import { getVersion } from "../../util/getVersion";

export const getCurrentlyOpenModal = (state) => state.currentlyOpenModal;
export const getBackButtonDestination = (state) => state.backButtonDestination;

export const getImageByFrame = (state, options = {}) => {
  // This one is not a _function_ of redux state alone!
  // Uses a global variable with Image objects.
  const { frame = getCurrentFrameIndex(state) } = options;
  const filename = getFilename(state, { frame });
  const url = getBestAvailableImageUrlByFilename(state, { filename });
  return window?.SirvHSE?.imageStore?.[url];
};

export const getPreviousFrame = (state, frameIndex) => {
  const fr = frameIndex ?? getCurrentFrameIndex(state);
  const N = getFrameCount(state);
  return mod(fr - 1, N);
};

export const getPointer = (state, inputLayer, inputFrame, hotspotIndex) => {
  const layer = inputLayer ?? getCurrentLayer(state);
  const frame = inputFrame ?? getCurrentFrameIndex(state);
  const currentHotspot = hotspotIndex ?? getCurrentHotspot(state);
  if (currentHotspot === null) {
    return null;
  }
  if (!hotspotVisibleInFrame(state, { frame, hotspotIndex: currentHotspot })) {
    return null;
  }
  return getCalculatedHotspotCoordinates(state, {
    layer,
    frame,
    hotspotIndex: currentHotspot,
  });
};

const getClickActionBoxObject = (state, options) => {
  const { hotspotIndex = getCurrentHotspot(state) } = options ?? {};
  const content = getClickActionBoxContent(state, hotspotIndex);
  if (!content) {
    return {};
  }
  const locationType = getClickActionBoxLocationType(state, options);
  let [fixed, x, y] = [false, 0, 0];
  if (locationType === "fixed") {
    fixed = true;
    const xKeyword = getClickActionBoxLocationFixedXKeyword(state, options);
    if (xKeyword === "offset") {
      x =
        getClickActionBoxLocationFixedXOffset(state, options)?.toString() ??
        "0";
    } else {
      x = xKeyword;
    }
    const yKeyword = getClickActionBoxLocationFixedYKeyword(state, options);
    if (yKeyword === "offset") {
      y =
        getClickActionBoxLocationFixedYOffset(state, options)?.toString() ??
        "0";
    } else {
      y = yKeyword;
    }
  } else if (locationType === "follow") {
    fixed = false;
    x =
      getClickActionBoxLocationFollowXOffset(state, options)?.toString() ?? "0";

    y =
      getClickActionBoxLocationFollowYOffset(state, options)?.toString() ?? "0";
  } else {
    throw new Error("unknown click action box location type " + locationType);
  }
  let out = {
    content,
    fixed,
    x,
    y,
  };
  const style = getBoxCssClassName(state, { hotspotIndex });
  if (style) {
    out.style = style;
  }
  return out;
};

export const getBoxCssClassName = (state, { hotspotIndex }) =>
  getBoxVariantCssClassName(state.boxVariant, { hotspotIndex }) ??
  getLoadedClickActionBoxStyle(state.boxVariant, { hotspotIndex });

/**
 * return a hotspot structure that Sirv Media Viewer(SMV) can understand
 * @param {object} state redux state object
 * @param {integer} hotspotIndex index of the hotspot for which we want to get the output
 * @returns {object} an object that when marshalled to JSON defines a hotspot for SMV
 */
export const getOneHotspot = (
  state,
  { hotspotIndex = getCurrentHotspot(state) }
) => {
  const N = getFrameCount(state);
  const algorithmOutputLayers = getAlgorithmOutput(state, { hotspotIndex });
  if (!Object.keys(algorithmOutputLayers).length) return {}; // necessary because lodash forEach will still enter the loop even with empty object
  const framesObject = {};
  forEach(algorithmOutputLayers, (layerContent, layerIndex) => {
    const frames = getVisibleFramesInLayer(state, {
      hotspotIndex,
      layer: layerIndex,
    });
    for (const frameIndex of frames) {
      // if(!(layerIndex in layerContent)){break}; handle manual mode
      const frameIndexInInfoSettingsFormat =
        parseInt(frameIndex) + 1 + parseInt(layerIndex) * N; //indexing for spin files
      const coordinatesInPercent = layerContent[frameIndex];
      const { x, y } = coordinatesInPercent;
      if (!isValidPercentage(x) || !isValidPercentage(y)) {
        console.error(
          `EXPORT ERROR: x:${x} y:${y} is not valid percentage! Do investigate`
        );
        return;
      }
      if (
        !frameIndexInInfoSettingsFormat ||
        frameIndexInInfoSettingsFormat < 0 ||
        frameIndexInInfoSettingsFormat > getFrameCount(state).length + 1
      ) {
        console.error(
          `EXPORT ERROR: output frame index value ${frameIndexInInfoSettingsFormat} is out of range! Do investigate`
        );
        return;
      }
      framesObject[frameIndexInInfoSettingsFormat] = {
        //TODO: if SMV API changes to consume frameIds change this line`
        pointer: {
          x: `${x}%`,
          y: `${y}%`,
        },
      };
    }
  });
  const hs = state.editor.present.hotspots[hotspotIndex];
  let out = {};
  const hoverActionType = getHoverActionType(state, hotspotIndex);
  if (hoverActionType !== hoverActions.none) {
    out[hoverActionType] = hs[hoverActionType];
  }
  const clickActionType = getClickActionType(state, hotspotIndex);
  // eslint-disable-next-line
  switch (clickActionType) {
    case "link":
      out.link = hs.link;
      break;
    case "box":
      out.box = getClickActionBoxObject(state, { hotspotIndex });
      break;
  }
  out.frames = framesObject;
  const name = getHotspotName(state, { hotspotIndex });
  if (name) {
    out.name = name;
  }
  const template = getTemplate(state, { hotspotIndex });
  if (template) {
    out.pointer = { template };
  }
  return out;
};

export const getHotspotsObject = (state) => {
  let hotspots = state.editor.present.hotspots.map((v, hotspotIndex) =>
    getOneHotspot(state, { hotspotIndex })
  );
  if (getVersion() === versions.image) {
    // correct for static image hotspot format
    hotspots.forEach((hs) => {
      hs.pointer = { ...hs.pointer, ...hs.frames?.[1]?.pointer }; //frames are one based in the JSON output
      delete hs.frames;
    });
  }
  return hotspots;
};

export const getStaticImageId = (state) => state.staticImageId;
export const getStaticImageSnippet = (state) => {
  const currentStaticImageSnippetType = getCurrentStaticImageSnippetType(state);
  const url = getSpinURL(state);
  const id = getStaticImageId(state);
  let hotspots = getHotspotsObject(state);
  if (!hotspots?.length) {
    return;
  }
  const hotspotString = offsetTextByConstant(
    JSON.stringify(hotspots, null, 1),
    3 //indentation at the level of insertion in the snippet below
  );
  if (currentStaticImageSnippetType === staticImageSnippetTypes.Script) {
    return `<script src="https://scripts.sirv.com/sirvjs/v3/sirv.js?modules=hotspots,lazyimage,zoom"></script>
<div id="${id}" class="Sirv" data-options="fullscreen.enable:false" ><div data-src="${url}"></div></div>
<script>
  Sirv.on("image:ready", (image) =>
  Sirv.getInstance("#${id}").child(0).image.hotspots.add(
    ${hotspotString}
    )
  )
</script>`;
  }

  if (currentStaticImageSnippetType === staticImageSnippetTypes.Inline) {
    return `<script src="https://scripts.sirv.com/sirvjs/v3/sirv.js?modules=hotspots,lazyimage,zoom"></script>
<script> var imageHotspots${id} = ${hotspotString};
</script>

<div class="Sirv" data-options="fullscreen.enable:false" >
  <div 
    data-src="${url}"
    data-hotspots="imageHotspots${id}"
  ></div>
</div>
`;
  }
};

export const getHotspotImportInProgress = (state) =>
  state.hotspotImport.inProgress;

export const getIsModified = (state) => {
  if (getHotspotImportInProgress(state)) {
    return false;
  }
  const hotspots = getHotspotsObject(state);
  const oldHotspots = state.spinInfo?.settings?.hotspots;
  if (!hotspots.length && !oldHotspots) {
    return false;
  }
  return !isEqual(hotspots, oldHotspots);
};

export const getSpinFileName = (state) =>
  decodeURIComponent(getSpinURL(state).split("/").pop());

export const getTemplate = (state, options) => {
  const { hotspotIndex } = options;
  // templates generated from user selection supersede the loaded templates
  // these include hotspot part styles and pointer text as "the second line of
  // defense"
  const templateFromVariantStyles = getTemplateFromVariantStyles(state, {
    hotspotIndex,
  });
  if (templateFromVariantStyles) {
    return templateFromVariantStyles;
  }
  const text = getPointerText(state, { hotspotIndex });
  if (text) {
    return text;
  }
  const hs = state.editor.present.hotspots?.[hotspotIndex];
  const template = hs?.template;
  return template;
};

export const getEditorData = (state) => {
  // IMPORTANT: Update doc/editorData.json.schema including the `example` field
  // and notify Olexander Shushunov on update to the output of this function
  return {
    // themes: getThemeData(state),
    styles: getVariantStyleData(state),
    hotspots: getHotspotsEditorData(state),
  };
};

export const getVariantStyleData = (state) => {
  return {
    pointer: {
      variant: getCurrentPointerVariant(state),
      color0: getPointerColor0(state),
      size: getPointerSize(state),
    },
    box: {
      variant: getCurrentBoxVariant(state),
      textColor: getBoxTextColor(state),
      backgroundColor: getBoxBackgroundColor(state),
    },
  };
};

export const getHotspotsEditorData = (state) => {
  return state.editor.present.hotspots.map((value, i) => value);
};

export const getThemeData = (state) => {
  const theme = getCommittedTheme(state);
  if (!theme) {
    return null;
  }
  const { name, version } = theme;
  return { theme: { name, version } };
};

//========= splitting this file for manageability ==================================

export * from "./variantStyles";

//========= reexported selectors as per Dan Abramov's idiomatic redux ==============
export const getSpinURL = (state) => fromSpinURL.getSpinURL(state.spinURL);
export const getUploadFilename = (state) =>
  fromSpinURL.getUploadFilename(state.spinURL);

export const getFolderURL = (state) =>
  fromFolderURL.getFolderURL(state.folderURL);
export const getHostname = (state) =>
  fromFolderURL.getHostname(state.folderURL);

export const getHotspotTabContent = (state) =>
  fromUI.getHotspotTabContent(state.ui);
export const getActiveHotspotDrag = (state) =>
  fromUI.getActiveHotspotDrag(state.ui);
export const getDragging = (state) => fromUI.getDragging(state.ui);
export const getOverwriteConfirmationRequired = (state) =>
  fromUI.getOverwriteConfirmationRequired(state.ui);
export const getShowInstructionHint = (state) =>
  fromUI.getShowInstructionHint(state.ui);
export const getAutoHint = (state) => fromUI.getAutoHint(state.ui);
export const getHotspotPathState = (state) =>
  fromUI.getHotspotPathState(state.ui);
export const getBeadState = (state, ...rest) =>
  fromUI.getBeadState(state.ui, ...rest);
export const getHighlightHotspotNumber = (state, ...rest) =>
  fromUI.getHighlightHotspotNumber(state.ui, ...rest);
export const getShowSpinHint = (state, ...rest) =>
  fromUI.getShowSpinHint(state.ui, ...rest);
export const getCurrentStaticImageSnippetType = (state, ...rest) =>
  fromUI.getCurrentStaticImageSnippetType(state.ui, ...rest);

// The editor slice is wrapped in redux-undo undoable, and its state
// has shape {past:[], present, future[]}
export const getNaturalAspect = (state) =>
  fromHotspotEditor.getNaturalAspect(state.editor.present);
export const getRedoDisabled = (state) => state.editor.future.length === 0;
export const getUndoDisabled = (state) => state.editor.past.length === 0;
export const getBestAvailableImageUrlByFilename = (state, ...rest) =>
  fromHotspotEditor.getBestAvailableImageUrlByFilename(
    state.editor.present,
    ...rest
  );
export const getBestRequestedImageKeysByFilename = (state, ...rest) =>
  fromHotspotEditor.getBestRequestedImageKeysByFilename(
    state.editor.present,
    ...rest
  );
export const getCommittedTheme = (state) =>
  fromHotspotEditor.getCommittedTheme(state.editor.present);
export const getPointerText = (state, ...rest) =>
  fromHotspotEditor.getPointerText(state.editor.present, ...rest);
export const getFrameCount = (state, ...rest) =>
  fromHotspotEditor.getFrameCount(state.editor.present, ...rest);
export const getCurrentHotspot = (state) =>
  fromHotspotEditor.getCurrentHotspot(state.editor.present);
export const getCurrentLayer = (state) =>
  fromHotspotEditor.getCurrentLayer(state.editor.present);
export const getHotspotName = (state, ...rest) =>
  fromHotspotEditor.getHotspotName(state.editor.present, ...rest);
export const getRequestedAlgorithm = (state, ...rest) =>
  fromHotspotEditor.getRequestedAlgorithm(state.editor.present, ...rest);
export const getActiveAlgorithm = (state, ...rest) =>
  fromHotspotEditor.getActiveAlgorithm(state.editor.present, ...rest);
export const getCheckpointKeyFrameCount = (state, ...rest) =>
  fromHotspotEditor.getCheckpointKeyFrameCount(state.editor.present, ...rest);
export const hotspotVisibleInFrame = (state, ...rest) =>
  fromHotspotEditor.hotspotVisibleInFrame(state.editor.present, ...rest);
export const getHidden = (state, ...rest) =>
  fromHotspotEditor.getHidden(state.editor.present, ...rest);
export const isWithinBounds = (state, ...rest) =>
  fromHotspotEditor.isWithinBounds(state.editor.present, ...rest);
export const getBounds = (state, ...rest) =>
  fromHotspotEditor.getBounds(state.editor.present, ...rest);
export const getKeyFrameSequentialIndices = (state, ...rest) =>
  fromHotspotEditor.getKeyFrameSequentialIndices(state.editor.present, ...rest);
export const getKeyFrameCount = (state, ...rest) =>
  fromHotspotEditor.getKeyFrameCount(state.editor.present, ...rest);
export const getFitReadyState = (state, ...rest) =>
  fromHotspotEditor.getFitReadyState(state.editor.present, ...rest);
export const getInstructions = (state, ...rest) =>
  fromHotspotEditor.getInstructions(state.editor.present, ...rest);
export const getCurrentStep = (state) =>
  fromHotspotEditor.getCurrentStep(state.editor.present);
export const getPreviousStep = (state) =>
  fromHotspotEditor.getPreviousStep(state.editor.present);
export const getWidth = (state) =>
  fromHotspotEditor.getWidth(state.editor.present);
export const getHeight = (state) =>
  fromHotspotEditor.getHeight(state.editor.present);
export const getCurrentFrameIndex = (state) =>
  fromHotspotEditor.getCurrentFrameIndex(state.editor.present);
export const getThumbnailFrameIndex = (state, ...rest) =>
  fromHotspotEditor.getThumbnailFrameIndex(state.editor.present, ...rest);
export const getVisibleFramesInLayer = (state, ...rest) =>
  fromHotspotEditor.getVisibleFramesInLayer(state.editor.present, ...rest);
export const getInvisibleFrames = (state, ...rest) =>
  fromHotspotEditor.getInvisibleFrames(state.editor.present, ...rest);
export const getCalculatedHotspotCoordinates = (state, ...rest) =>
  fromHotspotEditor.getCalculatedHotspotCoordinates(
    state.editor.present,
    ...rest
  );
export const getAlgorithmOutput = (state, ...rest) =>
  fromHotspotEditor.getAlgorithmOutput(state.editor.present, ...rest);
export const isAlgorithmSwitchPossible = (state, ...rest) =>
  fromHotspotEditor.isAlgorithmSwitchPossible(state.editor.present, ...rest);
export const getHotspotDisplayState = (state, ...rest) =>
  fromHotspotEditor.getHotspotDisplayState(state.editor.present, ...rest);
export const getHotspotNames = (state, ...rest) =>
  fromHotspotEditor.getHotspotNames(state.editor.present, ...rest);
export const getClickActionBoxLocationFixedYKeyword = (state, ...rest) =>
  fromHotspotEditor.getClickActionBoxLocationFixedYKeyword(
    state.editor.present,
    ...rest
  );
export const getClickActionBoxLocationFixedXKeyword = (state, ...rest) =>
  fromHotspotEditor.getClickActionBoxLocationFixedXKeyword(
    state.editor.present,
    ...rest
  );
export const getClickActionBoxLocationFixedYOffset = (state, ...rest) =>
  fromHotspotEditor.getClickActionBoxLocationFixedYOffset(
    state.editor.present,
    ...rest
  );
export const getClickActionBoxLocationFixedXOffset = (state, ...rest) =>
  fromHotspotEditor.getClickActionBoxLocationFixedXOffset(
    state.editor.present,
    ...rest
  );
export const getClickActionType = (state, ...rest) =>
  fromHotspotEditor.getClickActionType(state.editor.present, ...rest);
export const getClickActionLink = (state, ...rest) =>
  fromHotspotEditor.getClickActionLink(state.editor.present, ...rest);
export const getClickActionBoxContent = (state, ...rest) =>
  fromHotspotEditor.getClickActionBoxContent(state.editor.present, ...rest);
export const getClickActionBoxLocationFollowXOffset = (state, ...rest) =>
  fromHotspotEditor.getClickActionBoxLocationFollowXOffset(
    state.editor.present,
    ...rest
  );
export const getClickActionBoxLocationFollowYOffset = (state, ...rest) =>
  fromHotspotEditor.getClickActionBoxLocationFollowYOffset(
    state.editor.present,
    ...rest
  );
export const getHoverActionType = (state, ...rest) =>
  fromHotspotEditor.getHoverActionType(state.editor.present, ...rest);
export const getHoverActionTooltipContent = (state, ...rest) =>
  fromHotspotEditor.getHoverActionTooltipContent(state.editor.present, ...rest);
export const getHoverActionTooltipStyle = (state, ...rest) =>
  fromHotspotEditor.getHoverActionTooltipStyle(state.editor.present, ...rest);
export const getHotspotIndexOfClickMessageShown = (state, ...rest) =>
  fromHotspotEditor.getHotspotIndexOfClickMessageShown(
    state.editor.present,
    ...rest
  );
export const getHotspotIndexOfHoverMessageShown = (state, ...rest) =>
  fromHotspotEditor.getHotspotIndexOfHoverMessageShown(
    state.editor.present,
    ...rest
  );
export const getClickActionBoxLocationType = (state, ...rest) =>
  fromHotspotEditor.getClickActionBoxLocationType(
    state.editor.present,
    ...rest
  );
export const getFilenamesInLayer = (state, ...rest) =>
  fromHotspotEditor.getFilenamesInLayer(state.editor.present, ...rest);
export const getHotspotCount = (state, ...rest) =>
  fromHotspotEditor.getHotspotCount(state.editor.present, ...rest);
export const getFilename = (state, ...rest) =>
  fromHotspotEditor.getFilename(state.editor.present, ...rest);
export const getLoadedClickActionBoxStyle = (state, ...rest) =>
  fromHotspotEditor.getLoadedClickActionBoxStyle(state.editor.present, ...rest);
export const getAccepted = (state, ...rest) =>
  fromHotspotEditor.getAccepted(state.editor.present, ...rest);
export const getNotification = (state, ...rest) =>
  fromHotspotEditor.getNotification(state.editor.present, ...rest);

export const getCurrentPointerVariant = (state) =>
  fromPointerVariant.getCurrentPointerVariant(state.pointerVariant);
export const getPointerColor0 = (state) =>
  fromPointerVariant.getPointerColor0(state.pointerVariant);
export const getPointerSize = (state) =>
  fromPointerVariant.getPointerSize(state.pointerVariant);
export const getDefaultCoreHtml = (state, ...rest) =>
  fromPointerVariant.getDefaultCoreHtml(state.pointerVariant, ...rest);
export const getPointerCss = (state, ...rest) =>
  fromPointerVariant.getPointerCss(state.pointerVariant, ...rest);

export const getBoxVariants = globalizedBoxVariantSelectors.selectIds;
export const getCurrentBoxVariant = (state) =>
  fromBoxVariant.getCurrentBoxVariant(state.boxVariant);
export const getBoxTextColor = (state) =>
  fromBoxVariant.getBoxTextColor(state.boxVariant);
export const getBoxBackgroundColor = (state) =>
  fromBoxVariant.getBoxBackgroundColor(state.boxVariant);
export const getBoxCss = (state, ...rest) =>
  fromBoxVariant.getBoxCss(state.boxVariant, ...rest);
export const getBoxStyleTag = (state, ...rest) =>
  fromBoxVariant.getBoxStyleTag(state.boxVariant, ...rest);
